import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { PERIOD_STATUS_LABELS, can } from "@devengo/shared";

import { PeriodPicker } from "../components/period-picker";
import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  Notice,
  PageTitle,
  numClass,
  tableClass,
  thClass,
} from "../components/ui";
import { SendDocumentButton } from "../components/send-document";
import { VoidedNotice } from "../components/voided-notice";
import { api } from "../lib/api";
import {
  decimalToInput,
  formatCents,
  formatDateTime,
  parseUserAmount,
  periodName,
} from "../lib/format";
import { parsePeriod, periodParam, useCurrentUser } from "../lib/session";

/** One row of the Caja, built by the API (`cajaLayout`): the PDF prints the same rows. */
export interface CajaRow {
  label: string;
  net: number | null;
  vat: number | null;
  style: "line" | "heading" | "detail" | "subtotal" | "total" | "result";
  ruleAfter?: true;
}
interface VatPosition {
  saldoAPagarCents: number;
  voucherCount: number;
}
interface Snapshot {
  id: string;
  version: number;
  result: VatPosition;
  layout: CajaRow[];
  createdAt: string;
}
interface Preview {
  period: { id: string; status: keyof typeof PERIOD_STATUS_LABELS; label: string } | null;
  balancesTyped: boolean;
  balances?: {
    saldoTecnicoAnterior: string | null;
    saldoLibreDisponibilidadAnterior: string | null;
    retenciones: string | null;
  };
  result: VatPosition | null;
  layout?: CajaRow[];
  /** A closed or filed period shows the version it was closed with. */
  shownVersion?: number | null;
  latest: Snapshot | null;
  latestIsCurrent: boolean;
  /** Voided vouchers of the period, left out of the Caja (plan 024). */
  anulados?: { compra: number; venta: number };
}

export function CajaPage({
  companyId,
  search,
}: {
  companyId: string;
  search: { periodo?: string };
}) {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const client = useQueryClient();
  const { year, month } = parsePeriod(search.periodo);
  const periodo = periodParam(year, month);
  const preview = useQuery({
    queryKey: ["caja", companyId, periodo],
    queryFn: () =>
      api.get<Preview>(`/api/companies/${companyId}/periods/${year}/${month}/vat-position`),
  });
  const snapshots = useQuery({
    queryKey: ["caja", companyId, periodo, "snapshots"],
    queryFn: () =>
      api.get<Snapshot[]>(
        `/api/companies/${companyId}/periods/${year}/${month}/vat-position/snapshots`,
      ),
  });
  const [tecnico, setTecnico] = useState("");
  const [libre, setLibre] = useState("");
  const [retenciones, setRetenciones] = useState("");
  useEffect(() => {
    setTecnico(decimalToInput(preview.data?.balances?.saldoTecnicoAnterior ?? null));
    setLibre(decimalToInput(preview.data?.balances?.saldoLibreDisponibilidadAnterior ?? null));
    setRetenciones(decimalToInput(preview.data?.balances?.retenciones ?? null));
  }, [
    preview.data?.balances?.saldoTecnicoAnterior,
    preview.data?.balances?.saldoLibreDisponibilidadAnterior,
    preview.data?.balances?.retenciones,
  ]);

  const calculate = useMutation({
    mutationFn: () => {
      const t = parseUserAmount(tecnico);
      const l = parseUserAmount(libre);
      const r = parseUserAmount(retenciones);
      if (t === null || l === null || r === null) {
        throw new Error("Revisá los saldos y las retenciones: usá el formato 1.234,56.");
      }
      return api.post<Snapshot & { reused: boolean }>(
        `/api/companies/${companyId}/periods/${year}/${month}/vat-position`,
        { saldoTecnicoAnterior: t, saldoLibreDisponibilidadAnterior: l, retenciones: r },
      );
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["caja", companyId] });
      await client.invalidateQueries({ queryKey: ["periods", companyId] });
    },
  });

  const canCalculate = can(user.role, "calculateVatPosition");
  const data = preview.data;
  const result = data?.result;
  const closed = data?.period?.status === "cerrado" || data?.period?.status === "presentado";

  return (
    <>
      <PageTitle
        subtitle={
          <>
            {periodName(year, month)}
            {data?.period ? (
              <>
                {" "}
                ·{" "}
                <Badge tone={closed ? "warning" : "success"}>
                  {PERIOD_STATUS_LABELS[data.period.status]}
                </Badge>
              </>
            ) : null}
          </>
        }
        actions={
          <>
            <PeriodPicker
              value={periodo}
              onChange={(value) =>
                void navigate({
                  to: "/empresas/$companyId/caja",
                  params: { companyId },
                  search: { periodo: value },
                })
              }
            />
            {data?.latest ? (
              <a
                className="rounded-md border border-brand-line bg-white px-3 py-1.5 text-sm hover:bg-brand-tint"
                href={`/api/companies/${companyId}/periods/${year}/${month}/vat-position/snapshots/${data.latest.version}/pdf`}
              >
                Descargar PDF (v{data.latest.version})
              </a>
            ) : null}
            {data?.latest ? (
              <SendDocumentButton
                companyId={companyId}
                document={{
                  kind: "caja_iva_pdf",
                  year,
                  month,
                  snapshotVersion: data.latest.version,
                }}
              />
            ) : null}
          </>
        }
      >
        Caja IVA
      </PageTitle>

      <ErrorBox error={preview.error} />
      {!data?.period ? (
        <Card>
          <p className="text-sm text-brand-muted">
            No hay comprobantes en {periodName(year, month)}.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="flex flex-col gap-4 lg:col-span-2">
            <Notice tone={data.latestIsCurrent || data.shownVersion ? "success" : "info"}>
              {data.shownVersion
                ? `Período ${PERIOD_STATUS_LABELS[data.period.status].toLowerCase()}: se muestra la Caja IVA con la que se cerró (versión ${data.shownVersion}).`
                : data.latestIsCurrent
                  ? `La Caja IVA guardada (versión ${data.latest?.version}) coincide con los comprobantes actuales.`
                  : "Vista previa en vivo: se recalcula con cada comprobante. Guardala para dejar una versión firme."}
            </Notice>
            <VoidedNotice companyId={companyId} periodo={periodo} anulados={data.anulados} />
            {result && data.layout ? (
              <CajaTable rows={data.layout} voucherCount={result.voucherCount} />
            ) : null}
          </div>
          <div className="flex flex-col gap-4">
            <Card title="Saldos y retenciones">
              <div className="flex flex-col gap-3">
                <Field label="Saldo técnico a favor" hint="Del formulario 2051 del mes anterior">
                  <Input
                    className="text-right tabular"
                    value={tecnico}
                    onChange={(e) => setTecnico(e.target.value)}
                    placeholder="0,00"
                    disabled={closed || !canCalculate}
                  />
                </Field>
                <Field
                  label="Saldo de libre disponibilidad"
                  hint="Del formulario 2051 del mes anterior"
                >
                  <Input
                    className="text-right tabular"
                    value={libre}
                    onChange={(e) => setLibre(e.target.value)}
                    placeholder="0,00"
                    disabled={closed || !canCalculate}
                  />
                </Field>
                <Field
                  label="Retenciones de IVA del período"
                  hint="Sufridas en el mes; restan como pago a cuenta"
                >
                  <Input
                    className="text-right tabular"
                    value={retenciones}
                    onChange={(e) => setRetenciones(e.target.value)}
                    placeholder="0,00"
                    disabled={closed || !canCalculate}
                  />
                </Field>
                {!data.balancesTyped ? (
                  <Notice tone="warning">
                    Todavía no se cargaron los saldos ni las retenciones: la vista previa los toma
                    en cero.
                  </Notice>
                ) : null}
                <ErrorBox error={calculate.error} />
                {calculate.data?.reused ? (
                  <Notice>Sin cambios desde la versión {calculate.data.version}.</Notice>
                ) : null}
                <Button
                  disabled={!canCalculate || closed || calculate.isPending}
                  onClick={() => calculate.mutate()}
                >
                  Calcular y guardar versión
                </Button>
                {!canCalculate ? (
                  <p className="text-xs text-brand-muted">
                    Solo contadores y el dueño guardan la Caja IVA.
                  </p>
                ) : null}
              </div>
            </Card>
            <Card title="Versiones guardadas">
              {snapshots.data?.length ? (
                <ul className="flex flex-col gap-1 text-sm">
                  {snapshots.data.map((s) => (
                    <li key={s.id} className="flex items-center justify-between">
                      <span>
                        v{s.version} · {formatDateTime(s.createdAt)}
                      </span>
                      <span className="tabular font-medium">
                        {formatCents(s.result.saldoAPagarCents)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-brand-muted">Ninguna todavía.</p>
              )}
            </Card>
          </div>
        </div>
      )}
    </>
  );
}

/** The Caja in the studio's own layout: Concepto / Neto / I.V.A., a rule under each block. */
export function CajaTable({ rows, voucherCount }: { rows: CajaRow[]; voucherCount: number }) {
  const amount = (cents: number | null) => (cents === null ? "" : formatCents(cents));
  return (
    <Card title="Resumen general">
      <table className={tableClass}>
        <thead>
          <tr>
            <th className={thClass}>Concepto</th>
            <th className={`${thClass} text-right`}>Neto</th>
            <th className={`${thClass} text-right`}>I.V.A.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            // Not tdClass: its border on every row would hide which rows close a block.
            const rule = `px-2 py-1 align-top ${row.ruleAfter ? "border-b border-slate-400" : ""}`;
            const weight =
              row.style === "subtotal" || row.style === "total" || row.style === "result"
                ? "font-semibold"
                : "";
            const tone = row.style === "result" ? "text-base text-brand-primary" : "";
            const indent = row.style === "detail" || row.style === "heading" ? "pl-8" : "";
            const label = row.style === "heading" ? "italic text-brand-muted" : "";
            return (
              <tr key={i} className={`${weight} ${tone}`}>
                <td className={`${rule} ${indent} ${label}`}>{row.label}</td>
                <td className={`${rule} ${numClass}`}>{amount(row.net)}</td>
                <td className={`${rule} ${numClass}`}>{amount(row.vat)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-brand-muted">
        {voucherCount} comprobantes. Cálculo determinístico sobre lo cargado: en compras solo entran
        comprobantes A, que dan crédito fiscal.
      </p>
    </Card>
  );
}
